import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Network from '../../../models/Network.js';

import ReportListElement from '../list_elements/ReportListElement.js';
import FilterListElement from '../list_elements/FilterListElement.js';
import LoadingListElement from '../list_elements/LoadingListElement.js';
import './Module.css';

const key = process.env.REACT_APP_CLIENT_ACCESS_KEY;
const bypass = process.env.REACT_APP_BYPASS_LOGIN;
const URLS = require("../../../urls.js");

const StatusCodes = require('../../../status-codes/status-codes.js');

const MDReportsListFilteredModule = ({ user, isSmallScreen }) => {
    const navigate = useNavigate();

    const [jsonData, setJsonData] = useState(null);
    const [medicsData, setMedicsData] = useState(null);
    const [testMetadata, setTestMetadata] = useState(null);
    const [filterDict, setFilterDict] = useState(null);

    // Local-only filter state (do not share with user.browseState).
    const [filterState, setFilterState] = useState({
        tagTypes: [],
        medics: [],
        date: ["all"],
        customStartDate: "",
        customEndDate: "",
        measure: [],
        measureTrue: [],
        read: ["both"],
        finished: ["both"], // Finished & Unfinished
        status: ["Medical Director Review"],
        hasFeedback: false,
        showNarrativeSummary: false,
        incidentNumberSearch: ""
    });

    const [currentPage, setCurrentPage] = useState(0);
    const [reportsPerPage, setReportsPerPage] = useState(15);

    const handleNextPageButtonClick = () => setCurrentPage(currentPage + 1);
    const handlePrevPageButtonClick = () => setCurrentPage(currentPage - 1);

    const getSeveralReportsFiltered = async (desiredNumber, reportOffset, nextFilterDict) => {
        const url = URLS.GET_SEVERAL_REPORTS_FILTERED;
        const dict = {
            uid: user.uid,
            number_of_reports: desiredNumber,
            offset: reportOffset,
            filter_dict: nextFilterDict,
            CLIENT_ACCESS_KEY: key
        };

        let responseData;

        await Network.post(url, dict, (response) => {
            responseData = response.data;
            const status = response.status;

            if (Network.checkValidStatusAndResponse(response)) {
                // ok
            } else if (status == StatusCodes.INTERNAL_SERVER_ERROR) {
                console.log("Server Error");
            } else {
                console.log("Unknown Error");
            }
        });

        return responseData;
    };

    const getAllAgencyMedics = async (agencyId) => {
        const url = URLS.GET_MEDICS_FOR_USER;
        const dict = {
            uid: user.uid,
            agency_id: agencyId,
            CLIENT_ACCESS_KEY: key
        };

        let responseData;

        await Network.post(url, dict, (response) => {
            responseData = response.data;
            const status = response.status;

            if (Network.checkValidStatusAndResponse(response)) {
                // ok
            } else if (status == StatusCodes.INTERNAL_SERVER_ERROR) {
                console.log("Server Error");
            } else {
                console.log("Unknown Error");
            }
        });

        return responseData;
    };

    const markReportRead = async (reportId) => {
        const url = URLS.MARK_REPORT_READ_UNREAD;

        const dict = {
            uid: user.uid,
            report_id: reportId,
            read_status: 'read',
            CLIENT_ACCESS_KEY: key
        };

        let responseData;

        await Network.post(url, dict, (response) => {
            responseData = response.data;
            const status = response.status;

            if (Network.checkValidStatusAndResponse(response)) {
                // ok
            } else if (status == StatusCodes.INTERNAL_SERVER_ERROR) {
                console.log("Server Error");
            } else {
                console.log("Unknown Error");
            }
        });

        return responseData;
    };

    const getAllTestMetadata = () => {
        const url = URLS.GET_ALL_TEST_METADATA_URL;
        const dict = {
            uid: user.uid,
            CLIENT_ACCESS_KEY: key
        };

        return new Promise((resolve) => {
            Network.post(url, dict, (response) => {
                if (Network.checkValidStatusAndResponse(response)) {
                    resolve(Array.isArray(response.data) ? response.data : []);
                } else {
                    console.log('Error fetching test metadata, status:', response.status);
                    resolve([]);
                }
            });
        });
    };

    // Reset to first page when filters change.
    useEffect(() => {
        setCurrentPage(0);
    }, [filterState]);

    // Fetch reports when filters/page changes.
    useEffect(() => {
        if (filterDict) {
            getSeveralReportsFiltered(reportsPerPage, currentPage * reportsPerPage, filterDict)
                .then((data) => setJsonData(data))
                .catch((error) => console.log('Error:', error));
        }
    }, [filterDict, currentPage, reportsPerPage]);

    useEffect(() => {
        getAllAgencyMedics(user.uid)
            .then((data) => setMedicsData(data))
            .catch((error) => console.log('Error:', error));
    }, []);

    useEffect(() => {
        getAllTestMetadata()
            .then(setTestMetadata)
            .catch((error) => {
                console.log('Error fetching test metadata:', error);
                setTestMetadata([]);
            });
    }, []);

    const handleReportButtonClick = (reportId) => {
        if (bypass !== 'true') {
            markReportRead(reportId);
        }
        navigate('/report/' + reportId);
    };

    const createReportListElement = (reportId, reportNumber, incidentNumber, numberOfIssues, numberOfSuggestions, reportType, incidentTime, read, finished, narrativeSummarized) => {
        if (numberOfIssues === null) numberOfIssues = '-';
        if (numberOfSuggestions === null) numberOfSuggestions = '-';
        const showSummary = filterState.showNarrativeSummary === true;

        return (
            <ReportListElement
                reportNumber={reportNumber}
                incidentNumber={incidentNumber}
                numberOfIssues={numberOfIssues}
                numberOfSuggestions={numberOfSuggestions}
                reportType={reportType}
                incidentTime={incidentTime}
                read={read}
                finished={finished}
                narrativeSummarized={showSummary ? narrativeSummarized : undefined}
                verbose={showSummary}
                onButtonClick={() => handleReportButtonClick(reportId)}
            />
        );
    };

    return (
        <div className={isSmallScreen ? "standard-module-screen-small" : "standard-module"}>
            <FilterListElement
                medicsData={medicsData}
                testMetadata={testMetadata}
                filterState={filterState}
                dictUpdateHandler={setFilterDict}
                stateUpdateHandler={setFilterState}
                filterDropdownsVisible={{
                    finished: true,
                    status: true,
                    showNarrativeSummary: true
                }}
                showClearButton={false}
            />

            <div className="list-header">
                <div className='medium-div'>
                    <div style={{ fontWeight: "bold" }}> Incident # </div>
                </div>
                <div className='tiny-div'>
                    <div style={{ fontWeight: "bold" }}><i className="fa-solid fa-triangle-exclamation"></i></div>
                </div>
                <div className='tiny-div'>
                    <div style={{ fontWeight: "bold" }}><i className="fa-solid fa-message"></i></div>
                </div>
                <div className='large-div'>
                    <div style={{ fontWeight: "bold" }}> Run Type(s) </div>
                </div>
                <div className='medium-div'>
                    <div style={{ fontWeight: "bold" }}> Run Ended </div>
                </div>
                <button className='invisible-enter-button'> View Report </button>
            </div>

            {jsonData ? (
                jsonData.map((data) =>
                    createReportListElement(
                        data.report_id,
                        data.pcr_number,
                        data.incident_number,
                        data.number_issues,
                        data.number_suggestions,
                        data.report_type,
                        data.unit_back_in_service,
                        data.report_marked_read,
                        data.report_marked_finished,
                        data.narrative_summarized
                    )
                )
            ) : (
                <LoadingListElement height={100} backgroundColor="white" />
            )}

            <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', margin: 'auto', width: '40%', marginBottom: '20px' }}>
                <div className='small-div' style={{ justifyContent: 'center' }}>
                    {jsonData && currentPage > 0 ? (
                        <button className="box-button" onClick={() => handlePrevPageButtonClick()}>Prev Page</button>
                    ) : null}
                </div>
                <div className='small-div' style={{ justifyContent: 'center' }}>
                    <p>Page {currentPage + 1}</p>
                </div>
                <div className='small-div' style={{ justifyContent: 'center' }}>
                    {jsonData && jsonData.length === reportsPerPage ? (
                        <button className="box-button" onClick={() => handleNextPageButtonClick()}>Next Page</button>
                    ) : 'no more reports'}
                </div>
            </div>
        </div>
    );
};

export default MDReportsListFilteredModule;

